import { fontStack } from './fonts.js';
import type {
  Action,
  BlockNode,
  LayoutSnapshot,
  SnapshotNode,
  Theme,
  Viewport,
} from '@personalizehq/schemas/ir';
import { a11yLabel, isA11yElement, type Ctx } from '@personalizehq/schemas/measure';

// THE PAINTER. Positions every node from the solver's computed rects (the same architecture as
// the iOS/Android/Flutter renderers) and makes NO layout decisions of its own. CSS is used for
// PAINTING ONLY — the layout laws all live in @personalizehq/schemas/measure, and the CSS-flow
// implementation that delegates layout to the browser lives in spike/web as the independent
// cross-check.
//
// DOM shape: nested per the resolved tree, each element `position: absolute` at
// (frame − parentFrame), `data-phq-id` on everything. Sibling order = snapshot pre-order =
// paint order, so box overlays stack correctly with zero z-index management.

export interface PaintHooks {
  onAction: (action: Action, nodeId: string) => void;
  dismiss: () => void;
  /** Conformance runs against fixtures whose media URLs are placeholders. Loading them fires a
   *  real request per node, per viewport, per engine — slow, noisy, and offline-fragile. Layout
   *  never depends on the bytes (aspect_ratio reserves the box), so the harness skips them. */
  loadMedia?: boolean | undefined;
}

const px = (n: number) => `${n}px`;

function colorOf(theme: Theme, token: string, vp: Viewport): string {
  const c = theme.colors[token];
  return c ? c[vp.color_scheme] : token;
}

/** The shared style (SPEC §5.0), applied WITHOUT touching geometry.
 *
 *  `border` is an INSET RING (`box-shadow: inset`), never CSS `border`: under this renderer the
 *  frames are imposed so border-box could not move layout anyway, but the inset ring keeps ONE
 *  visual law across both web arms (the CSS-flow harness arm must not use `border` — it would
 *  inset content there). `shadow` (outer) composes into the same declaration. */
function applyStyle(
  el: HTMLElement,
  n: BlockNode,
  theme: Theme,
  vp: Viewport
): void {
  const s = n.style;
  if (!s) return;
  if (typeof s.bg === 'string') {
    el.style.background = colorOf(theme, s.bg, vp);
  } else if (s.bg) {
    // A GRADIENT (SPEC §5.0c). Its stops are TOKENS, and CSS's `linear-gradient(Ndeg, ...)` uses
    // the same "degrees clockwise from up" convention the IR does — deliberately, so the editor,
    // the web renderer and CAGradientLayer all mean one thing by `angle`.
    //
    // The resolved stops are STAMPED: `background-color` is transparent under a gradient, so
    // `getComputedStyle` gives the snapshot nothing, and a light/dark gradient pair would be
    // byte-identical with the colour scheme gating nothing.
    const stops = s.bg.stops.map((t) => colorOf(theme, t, vp));
    el.style.backgroundImage = `linear-gradient(${s.bg.angle}deg, ${stops.join(', ')})`;
    el.dataset.phqBgStops = stops.join('|');
  }
  if (s.radius !== undefined) {
    // One radius, or four. PHYSICAL corners — a rounded card does not mirror in RTL, so this is
    // `border-radius`, never the logical `border-start-start-radius` family.
    el.style.borderRadius =
      typeof s.radius === 'number'
        ? px(s.radius)
        : `${px(s.radius.tl)} ${px(s.radius.tr)} ${px(s.radius.br)} ${px(s.radius.bl)}`;
    // Radius clips — matching native clipsToBounds — but the `overflow` SHORTHAND would reset
    // the per-axis overflow a `scroll` sets, silently making a rounded carousel unscrollable.
    // And clipping TEXT would hide the loud-overflow the no-shrink law exists to expose
    // (SPEC §6.2). A scroll's own overflow already clips to its radius.
    if (n.block !== 'scroll' && n.block !== 'text') el.style.overflow = 'hidden';
  }
  const shadows: string[] = [];
  if (s.border) {
    const c = colorOf(theme, s.border.color, vp);
    shadows.push(`inset 0 0 0 ${px(s.border.width)} ${c}`);
    el.dataset.phqBorderColor = c;
  }
  if (s.shadow) {
    const c = colorOf(theme, s.shadow.color, vp);
    // `x` and `spread` default to 0 — the v1.0 shape had neither, so an old document still paints
    // exactly the shadow it painted before.
    const sh = s.shadow;
    shadows.push(`${px(sh.x ?? 0)} ${px(sh.y)} ${px(sh.blur)} ${px(sh.spread ?? 0)} ${c}`);
    el.dataset.phqShadowColor = c;
  }
  if (shadows.length > 0) el.style.boxShadow = shadows.join(', ');
  if (s.opacity !== undefined) el.style.opacity = String(s.opacity);
  // FROSTED GLASS (SPEC §5.0e). Capability-gated at publish, so by the time a document reaches a
  // renderer that cannot do this, it has already been swapped for its fallback.
  if (s.backdrop) el.style.backdropFilter = `blur(${px(s.backdrop.blur)})`;
}

/** REDUCED MOTION IS NOT OPTIONAL (SPEC §5.0e). A user who has asked the OS to stop moving things
 *  has usually asked because motion makes them ill. Entry, stagger and parallax are all skipped —
 *  the block simply appears, at the rect the solver already computed. */
const reducedMotion = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** ENTRY + STAGGER (SPEC §5.0e). `delay` is the block's own delay PLUS its index times the
 *  parent's `stagger_ms` — which is why a strip that cascades is ONE field on the strip, and stays
 *  right when a card is added. */
function animateIn(el: HTMLElement, n: BlockNode, index: number, parentStagger: number): void {
  const entry = n.style?.animate?.entry;
  if (!entry || reducedMotion()) return;

  const delay = (entry.delay_ms ?? 0) + index * parentStagger;
  const from =
    entry.kind === 'fade_up'
      ? { opacity: 0, transform: 'translateY(12px)' }
      : entry.kind === 'scale_in'
        ? { opacity: 0, transform: 'scale(0.92)' }
        : { opacity: 0, transform: 'none' };

  // A WEB ANIMATION, not a CSS transition: it composites off the main thread, it cannot be
  // disturbed by a later style write, and — the point — IT DOES NOT TOUCH LAYOUT. `opacity` and
  // `transform` are the only two properties that never reflow, which is what keeps the promise
  // that motion cannot move a rect the solver computed.
  el.animate(
    [from, { opacity: n.style?.opacity ?? 1, transform: 'none' }],
    { duration: entry.duration_ms, delay, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'backwards' }
  );
}

/** RTL flips text alignment, not just frames. SPEC §6.9's mirror moves BOXES; the glyphs inside
 *  an `align: start` text would still start at the LEFT edge under rects positioning — invisible
 *  to conformance (frames and line_count don't move), visible to every RTL user. All four rects
 *  renderers share this law. */
function textAlign(
  align: 'start' | 'center' | 'end' | undefined,
  vp: Viewport
): string {
  const a = align ?? 'start';
  if (a === 'center') return 'center';
  const flipped = vp.direction === 'rtl' ? (a === 'start' ? 'end' : 'start') : a;
  return flipped === 'end' ? 'right' : 'left';
}

export interface PaintResult {
  root: HTMLElement;
  /** Custom-component cleanups + pending painter invocations, run by mount(). */
  customMounts: Array<{ el: HTMLElement; identifier: string; config: unknown; nodeId: string }>;
}

export function paint(
  tree: BlockNode,
  snapshot: LayoutSnapshot,
  theme: Theme,
  vp: Viewport,
  ctx: Ctx,
  hooks: PaintHooks
): PaintResult {
  const frames = new Map<string, SnapshotNode>(snapshot.nodes.map((n) => [n.id, n]));
  const customMounts: PaintResult['customMounts'] = [];

  function el(
    n: BlockNode,
    parentFrame: [number, number],
    index = 0,
    parentStagger = 0
  ): HTMLElement | null {
    const snap = frames.get(n.id);
    // Not in the snapshot => resolve()/expand() dropped it (it should not have been passed in)
    // or it is a repeat (which expand() replaced). Nothing to paint.
    if (!snap) return null;
    const [x, y, w, h] = snap.frame;

    const div = document.createElement('div');
    div.dataset.phqId = n.id;
    div.dataset.phqKind = n.block;
    div.style.position = 'absolute';
    div.style.left = px(x - parentFrame[0]);
    div.style.top = px(y - parentFrame[1]);
    div.style.width = px(w);
    div.style.height = px(h);
    div.style.boxSizing = 'border-box';

    const stagger = n.style?.animate?.stagger_ms ?? 0;

    switch (n.block) {
      case 'stack':
      case 'box':
      case 'grid': {
        n.children.forEach((c, i) => {
          const kid = el(c, [x, y], i, stagger);
          if (kid) div.appendChild(kid);
        });
        break;
      }
      case 'scroll': {
        // SPEC §6.7: content is laid out UNBOUNDED along `axis`. Under rects positioning that
        // needs an inner content element, because (a) only positive-direction overflow is
        // scrollable — in RTL a horizontal rail's mirrored children extend to the LEFT of the
        // frame and would be unreachable — and (b) the scrollable extent must be deterministic.
        // Children position at (childFrame − contentOrigin); the initial scroll offset
        // (frame.origin − contentOrigin) makes at-rest DOM rects equal the solver frames in
        // BOTH directions.
        const vertical = n.axis === 'vertical';
        div.style.overflowX = vertical ? 'hidden' : 'auto';
        div.style.overflowY = vertical ? 'auto' : 'hidden';
        if (n.snap) {
          div.style.scrollSnapType = `${vertical ? 'y' : 'x'} mandatory`;
        }
        div.dataset.phqScrolls = n.axis;

        const kidFrames = n.children
          .map((c) => frames.get(c.id))
          .filter((f): f is SnapshotNode => f !== undefined);
        const originX = vertical ? x : Math.min(x, ...kidFrames.map((f) => f.frame[0]));
        const originY = vertical ? Math.min(y, ...kidFrames.map((f) => f.frame[1])) : y;
        const extentX = vertical
          ? w
          : Math.max(x + w, ...kidFrames.map((f) => f.frame[0] + f.frame[2])) - originX;
        const extentY = vertical
          ? Math.max(y + h, ...kidFrames.map((f) => f.frame[1] + f.frame[3])) - originY
          : h;

        const content = document.createElement('div');
        content.style.position = 'relative';
        content.style.width = px(extentX);
        content.style.height = px(extentY);
        const parallaxed: Array<{ el: HTMLElement; factor: number }> = [];
        n.children.forEach((c, i) => {
          const kid = el(c, [originX, originY], i, stagger);
          if (kid) {
            if (n.snap) kid.style.scrollSnapAlign = 'start';
            const f = c.style?.animate?.parallax;
            if (f !== undefined) parallaxed.push({ el: kid, factor: f });
            content.appendChild(kid);
          }
        });
        div.appendChild(content);

        // PARALLAX IS SCROLL-DRIVEN, NOT TIME-DRIVEN (SPEC §5.0e): a function of THIS scroll's
        // offset, not of the clock. It translates only — the solver's rect is the at-rest position
        // and nothing here may move it.
        //
        // Bound to the NEAREST ANCESTOR SCROLL, which under this DOM shape is simply the element
        // we are building: a parallax child is registered by its own parent, so "nearest" needs no
        // tree walk at all.
        if (parallaxed.length > 0 && !reducedMotion()) {
          const at0 = vertical ? Number(div.dataset.phqScrollTop) : Number(div.dataset.phqScrollLeft);
          const apply = (): void => {
            const off = (vertical ? div.scrollTop : div.scrollLeft) - at0;
            for (const p of parallaxed) {
              const d = -off * p.factor;
              p.el.style.transform = vertical ? `translateY(${px(d)})` : `translateX(${px(d)})`;
            }
          };
          div.addEventListener('scroll', apply, { passive: true });
          apply();
        }
        // Set after insertion into the document by mount() — a detached element cannot scroll.
        div.dataset.phqScrollLeft = String(originX < x ? x - originX : 0);
        div.dataset.phqScrollTop = String(originY < y ? y - originY : 0);
        break;
      }
      case 'text': {
        // OUTER = the imposed solver frame. INNER = natural flow height — the DOM's OWN wrap.
        // Without the inner element the conformance line_count gate is circular: measuring the
        // outer box just reads the solver's answer back. If DOM wrap ever disagrees with the
        // canvas measurement, the text paints past the frame bottom — visible and gated, never
        // silently clipped.
        const f = ctx.font(n.font);
        div.style.font = `${f.weight} ${px(f.size)}/${px(f.lineHeight)} ${fontStack(f.family)}`;
        div.style.color = colorOf(theme, n.color, vp);
        div.style.textAlign = textAlign(n.align, vp);
        const inner = document.createElement('div');
        inner.style.display = 'block';
        inner.style.width = '100%';
        inner.style.overflowWrap = 'break-word'; // the §6.6 law, so a disagreement BREAKS not escapes
        inner.textContent = ctx.text(n.spans);
        // max_lines (SPEC §6.11) clamps THE INNER, never the outer: snapshot.ts derives line_count
        // from the inner's own height, so an unclamped inner would report the NATURAL count while
        // the solver reported the capped one — and line_count is an EXACT gate.
        if (n.max_lines !== undefined) {
          inner.style.display = '-webkit-box';
          inner.style.webkitBoxOrient = 'vertical';
          inner.style.webkitLineClamp = String(n.max_lines);
          inner.style.overflow = 'hidden';
        }
        div.appendChild(inner);
        break;
      }
      case 'button': {
        const v = theme.buttons[n.variant];
        const f = ctx.font(n.font);
        div.style.font = `${f.weight} ${px(f.size)}/${px(f.lineHeight)} ${fontStack(f.family)}`;
        div.style.textAlign = 'center';
        div.style.whiteSpace = 'nowrap';
        div.style.overflow = 'hidden';
        div.style.cursor = 'pointer';
        if (v) {
          div.style.color = colorOf(theme, v.fg, vp);
          if (v.bg) div.style.background = colorOf(theme, v.bg, vp);
          div.style.borderRadius = px(v.radius);
          // PADDING IS LOGICAL (start/end), never physical. The shorthand `padding: t e b s`
          // hard-codes start->left, which is wrong in RTL whenever start != end. Third member of
          // the §6.9 family: the mirror moves BOXES, and a renderer owes it GLYPHS and INSETS.
          const startSide = vp.direction === 'rtl' ? 'Right' : 'Left';
          const endSide = vp.direction === 'rtl' ? 'Left' : 'Right';
          div.style.paddingTop = px(v.padding.top);
          div.style.paddingBottom = px(v.padding.bottom);
          div.style[`padding${startSide}` as 'paddingLeft'] = px(v.padding.start);
          div.style[`padding${endSide}` as 'paddingLeft'] = px(v.padding.end);
        }
        div.textContent = ctx.text(n.label);
        break;
      }
      case 'image':
      case 'video': {
        const media =
          n.block === 'image'
            ? document.createElement('img')
            : document.createElement('video');
        if (hooks.loadMedia !== false) media.src = n.source.url;
        // ALWAYS `alt=""`. The NAME lives on the wrapper div (`aria-label`, from `a11y_label`) —
        // an <img> with no alt at all is announced by its filename, which is how a screen reader
        // ends up reading "hero-2x-final-v3-dot-p-n-g" to someone. Empty alt = "ignore me", and the
        // wrapper says what this actually is (SPEC §5.0a).
        if (n.block === 'image') (media as HTMLImageElement).alt = '';
        media.style.width = '100%';
        media.style.height = '100%';
        // `fit` is an image-only field in the IR; video always covers its aspect-ratio box.
        media.style.objectFit = n.block === 'image' ? n.fit : 'cover';
        // FOCAL (SPEC §5.0d). `object-position` IS the focal point: `cover` crops from the centre,
        // which is exactly where faces are not. The IR's 0..1 is a fraction of the OVERFLOW, which
        // is precisely what a percentage means to `object-position`.
        if (n.block === 'image' && n.focal) {
          media.style.objectPosition = `${n.focal.x * 100}% ${n.focal.y * 100}%`;
        }
        media.style.display = 'block';
        if (n.radius) {
          media.style.borderRadius = px(n.radius);
          div.style.borderRadius = px(n.radius);
          div.style.overflow = 'hidden';
        }
        if (n.block === 'video') {
          const v = media as HTMLVideoElement;
          v.muted = n.muted ?? false;
          v.loop = n.loop ?? false;
          v.autoplay = n.autoplay ?? false;
          v.playsInline = true;
          if (n.poster) v.poster = n.poster;
        }
        div.appendChild(media);
        if (n.block === 'image' && n.tint) {
          // Tint = a colour overlay at the image's frame. Asserted as a resolved VALUE by
          // conformance; the visual treatment is paint-only.
          const tintC = colorOf(theme, n.tint, vp);
          const overlay = document.createElement('div');
          overlay.style.position = 'absolute';
          overlay.style.inset = '0';
          overlay.style.background = tintC;
          overlay.style.mixBlendMode = 'multiply';
          overlay.style.pointerEvents = 'none';
          div.appendChild(overlay);
          div.dataset.phqTint = tintC;
        }
        break;
      }
      case 'progress': {
        // The segments EXIST AS SNAPSHOT NODES (`${id}_seg${i}`, pushed by layoutProgress), so
        // POSITION THEM FROM THEIR FRAMES like every other node — never re-derive them. Deriving
        // them left-to-right with divide() looked right and was wrong: the solver MIRRORS the
        // whole snapshot in RTL, so a re-derived segment ignores the mirror and lands on the
        // opposite side of the bar. Caught by custom_adversarial @ rtl on the first golden run.
        for (let i = 0; i < n.segments; i++) {
          const sf = frames.get(`${n.id}_seg${i}`);
          if (!sf) continue;
          const seg = document.createElement('div');
          seg.dataset.phqId = `${n.id}_seg${i}`;
          seg.style.position = 'absolute';
          seg.style.left = px(sf.frame[0] - x);
          seg.style.top = px(sf.frame[1] - y);
          seg.style.width = px(sf.frame[2]);
          seg.style.height = px(sf.frame[3]);
          seg.style.borderRadius = '999px';
          // TWO SEMANTICS (SPEC §6.10): a progress BAR lights a PREFIX ("step 2 of 3"); a PAGE
          // INDICATOR (one that `tracks` a scroll) lights exactly ONE — the card you are looking at.
          const lit = n.tracks === undefined ? i < n.value : i === n.value - 1;
          seg.style.background = colorOf(theme, lit ? n.fill_color : n.track_color, vp);
          div.appendChild(seg);
        }
        break;
      }
      case 'custom': {
        // The host's registered painter gets the element AFTER insertion (mount() invokes it) —
        // it receives a FIXED rect and contributes nothing to layout (SPEC §8).
        customMounts.push({ el: div, identifier: n.identifier, config: n.config, nodeId: n.id });
        break;
      }
      case 'spacer':
        break;
      /* v8 ignore next 2 -- the tree is post-resolve(): repeat cannot appear */
      case 'repeat':
        break;
    }

    // ANY BLOCK IS TAPPABLE (SPEC §5.0). One place, after the switch, so a new block cannot forget
    // to be — this used to live inside `case 'button'` and nothing else could be tapped.
    //
    // NESTED TAPPABLES: THE INNERMOST WINS. A child div IS a DOM descendant of its container, so a
    // tap on a button inside a tappable card would bubble and fire BOTH. `stopPropagation` is what
    // buys the law that iOS gets for free from hit-testing. Getting this wrong is silent and awful:
    // one platform firing two actions from one tap.
    //
    // The scrim is a SIBLING of the painted layer (surfaces.ts) and self-guards on `e.target`, so
    // stopping propagation in here cannot break tap-to-dismiss.
    // A11Y (SPEC §5.0a). We had NOTHING here: a tappable card was an anonymous div, and every image
    // was an <img> with no alt.
    //
    // A TAPPABLE BLOCK IS ONE ACCESSIBILITY ELEMENT — role=button, focusable, named, and its
    // subtree hidden from the AT tree. The alternative is a screen reader reading a card as four
    // unrelated fragments with no hint that any of it can be tapped. This is the a11y half of A3's
    // innermost-wins: one tap target, one announcement.
    if (n.a11y_hidden) {
      div.setAttribute('aria-hidden', 'true');
    } else if (isA11yElement(n)) {
      div.setAttribute('role', 'button');
      div.tabIndex = 0;
      const label = a11yLabel(n, ctx);
      if (label) div.setAttribute('aria-label', label);
      // The children are already IN the label (a11yLabel joins the subtree's text). Announcing them
      // again would read the card twice.
      for (const kid of Array.from(div.children)) kid.setAttribute('aria-hidden', 'true');
    } else {
      const label = a11yLabel(n, ctx);
      // `text` labels itself by its own content — an aria-label there is redundant. An IMAGE is the
      // case that matters: silent unless the author names it (SPEC §5.0a).
      if (label && n.block !== 'text') div.setAttribute('aria-label', label);
      if (!label && (n.block === 'image' || n.block === 'video')) {
        // SILENT, not "image". An unlabelled decorative image announced as "image" is noise.
        div.setAttribute('aria-hidden', 'true');
      }
    }

    if (n.actions?.length) {
      const actions = n.actions;
      div.style.cursor = 'pointer';
      div.addEventListener('click', (e) => {
        e.stopPropagation();
        // SPEC §9: an unknown action type is a NO-OP — skip it, keep going. It must not stop the
        // actions AFTER it: a new type shipped to an old binary cannot disable the `dismiss`
        // sitting next to it.
        for (const a of actions) {
          hooks.onAction(a, n.id);
          if (a.type === 'dismiss') hooks.dismiss();
        }
      });
    }

    applyStyle(div, n, theme, vp);
    animateIn(div, n, index, parentStagger);
    return div;
  }

  const rootSnap = frames.get(tree.id);
  const root = el(tree, [0, 0]);
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.inset = '0';
  // The wrapper spans the layer, so it must stay transparent to pointers too — only the
  // PAINTED document opts back in. This is what leaves the scrim reachable everywhere the
  // experience does not actually draw, and lets a banner pass clicks through to the page.
  wrapper.style.pointerEvents = 'none';
  if (root && rootSnap) {
    // The root keeps its solver position (centred origin) within the layer.
    root.style.left = px(rootSnap.frame[0]);
    root.style.top = px(rootSnap.frame[1]);
    root.style.pointerEvents = 'auto';
    wrapper.appendChild(root);
  }
  return { root: wrapper, customMounts };
}

/** Applies the deferred initial scroll offsets — must run AFTER the tree is in the document
 *  (a detached element cannot scroll). This is what makes RTL horizontal rails reachable. */
export function applyScrollOffsets(layer: HTMLElement): void {
  for (const el of Array.from(layer.querySelectorAll<HTMLElement>('[data-phq-scroll-left]'))) {
    el.scrollLeft = parseFloat(el.dataset.phqScrollLeft ?? '0');
    el.scrollTop = parseFloat(el.dataset.phqScrollTop ?? '0');
  }
}
